/**
 * Elido Cookie Consent Store
 * --------------------------
 * Centralised, framework-agnostic consent management following the Swedish
 * "cookielag"/PTS guidance + ePrivacy/GDPR:
 *
 *  - Necessary cookies are always allowed (no consent needed).
 *  - Statistics + Marketing require EXPLICIT, opt-in consent.
 *  - No tracking scripts (GA, Google Ads, Meta Pixel) may be loaded before
 *    the appropriate category has been accepted.
 *
 * This module exposes:
 *   - getConsent()     → current categories { necessary, statistics, marketing }
 *   - hasDecision()    → true when the user has actively made any choice
 *   - setConsent(obj)  → persist + notify listeners
 *   - onConsentChange(fn) / offConsentChange(fn)
 *   - openSettings()   → dispatches an event that re-opens the banner modal
 *
 * Downstream tracking integrations should subscribe with onConsentChange and
 * only inject their <script> when the relevant category is true.
 */

const STORAGE_KEY = "elido_cookie_consent_v1";
const EVENT_UPDATED = "elido:consent-updated";
const EVENT_OPEN = "elido:open-cookie-settings";

export const CONSENT_CATEGORIES = ["necessary", "statistics", "marketing"];

const DEFAULT = { necessary: true, statistics: false, marketing: false };

function safeStorage() {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
}

export function getConsent() {
  const s = safeStorage();
  if (!s) return { ...DEFAULT };
  try {
    const raw = s.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT };
    const parsed = JSON.parse(raw);
    return {
      necessary: true, // non-negotiable
      statistics: !!parsed?.statistics,
      marketing: !!parsed?.marketing,
      updated_at: parsed?.updated_at || null,
      version: parsed?.version || 1,
    };
  } catch {
    return { ...DEFAULT };
  }
}

export function hasDecision() {
  const s = safeStorage();
  if (!s) return false;
  try {
    return !!s.getItem(STORAGE_KEY);
  } catch {
    return false;
  }
}

export function setConsent({ statistics = false, marketing = false } = {}) {
  const payload = {
    necessary: true,
    statistics: !!statistics,
    marketing: !!marketing,
    updated_at: new Date().toISOString(),
    version: 1,
  };
  const s = safeStorage();
  if (s) {
    try {
      s.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {
      /* storage full / private mode — ignore */
    }
  }
  // Consent Mode v2 — mappa till Googles consent-signaler. Detta är
  // det enda stället som skickar `consent update`; det körs varje gång
  // användaren väljer (både i banner och i cookieinställningarna).
  if (typeof window !== "undefined") {
    try {
      window.dataLayer = window.dataLayer || [];
      const stat = payload.statistics ? "granted" : "denied";
      const mkt = payload.marketing ? "granted" : "denied";
      const push = (args) => window.dataLayer.push(args);
      // Prefer window.gtag om det finns (garanterar rätt argsQueue-format)
      const gtag = window.gtag || function () { push(arguments); };
      gtag("consent", "update", {
        ad_storage: mkt,
        ad_user_data: mkt,
        ad_personalization: mkt,
        analytics_storage: stat,
      });
      gtag("set", "ads_data_redaction", mkt === "granted" ? false : true);
    } catch {
      /* aldrig krascha på tracking */
    }
    try {
      window.dispatchEvent(new CustomEvent(EVENT_UPDATED, { detail: payload }));
    } catch {
      /* ignore */
    }
  }
  return payload;
}

export function acceptAll() {
  return setConsent({ statistics: true, marketing: true });
}

export function rejectAll() {
  return setConsent({ statistics: false, marketing: false });
}

/**
 * Completely clear the stored decision so the banner re-appears. Useful for
 * the "Cookieinställningar"-link in the footer when users want to change
 * their mind — we simply dispatch EVENT_OPEN and the React banner subscribes.
 */
export function openSettings() {
  if (typeof window !== "undefined") {
    try {
      window.dispatchEvent(new CustomEvent(EVENT_OPEN));
    } catch {
      /* ignore */
    }
  }
}

/**
 * Consent-fält för lead-payloads. Separerar leadets driftdata (vilket
 * samtyckesval som gällde vid submit) från marknadsattributionen. Skickas
 * av alla webbformulär som redan använder attribution. Backend härleder
 * attribution_status från dessa + attributionen.
 */
export function getConsentLeadFields() {
  const c = getConsent();
  return {
    statistics_consent: !!c.statistics,
    marketing_consent: !!c.marketing,
    consent_decision: hasDecision(),
  };
}

export function onConsentChange(listener) {
  if (typeof window === "undefined" || typeof listener !== "function") return;
  window.addEventListener(EVENT_UPDATED, listener);
}

export function offConsentChange(listener) {
  if (typeof window === "undefined" || typeof listener !== "function") return;
  window.removeEventListener(EVENT_UPDATED, listener);
}

export const CONSENT_EVENTS = { UPDATED: EVENT_UPDATED, OPEN: EVENT_OPEN };
